import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@munifocus/ui';
import type {
  CreateComplaintDto,
  CreateFileDto,
  ComplaintDetail,
  FileActivityEntry,
  FileDetail,
  FileListSort,
  FileSummary,
  Paginated,
  SaveStageDataDto,
  UpdateComplaintDto,
  UpdateFileDto,
} from '@munifocus/contracts-gov';
import { MAX_FILE_PAGE_SIZE } from '@munifocus/contracts-gov';
import { api } from './api-client';
import { stageLabelKey } from './file-display';
import { apiErrorMessage, apiErrorStatus } from './mutation-toast';
import { deadlinesKeys } from './deadlines-keys';
import { adminKeys } from './administration-api';
import { triageKeys } from './triage-api';

/**
 * The toast every stage write shares.
 *
 * Lives with the mutations rather than in each screen: a stage that advances
 * silently is indistinguishable from one that failed, and there are eight
 * screens that advance stages — putting the confirmation in each of them is how
 * six of them end up without one. Names the file and where it landed, because
 * "Saved." answers neither question an officer has.
 */
function useStageToast() {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  return {
    saved(file: FileDetail, advanced: boolean) {
      if (advanced) {
        snackbar.success(
          t('gov.stage.advancedToast', {
            reference: file.reference,
            stage: t(stageLabelKey(file.stage)),
          }),
        );
      } else {
        snackbar.success(t('gov.stage.savedToast', { reference: file.reference }));
      }
    },
    /**
     * The API's coded reason when it gave one — an unserved notice, a file that
     * moved on in another tab — and the generic line otherwise.
     */
    failed(err: unknown) {
      snackbar.error(apiErrorMessage(err, t('gov.stage.saveError')));
    },
  };
}

/**
 * A 409 from a stage write means the file is not where this screen thinks it
 * is. Refetch it, so the officer is looking at the stage it is actually at
 * rather than retrying against a stale one.
 */
function refetchOnConflict(qc: ReturnType<typeof useQueryClient>, id: string, err: unknown) {
  if (apiErrorStatus(err) === 409) void qc.invalidateQueries({ queryKey: filesKeys.detail(id) });
}

// --- raw API calls ---------------------------------------------------------

/**
 * The caller's reachable files, up to the API's page cap.
 *
 * Every consumer — the dashboard queue and its counts, the field map, the
 * inspection-freshness panel, the evidence picker — needs the whole caseload,
 * not a page of it. Without an explicit size the API returned twenty, and the
 * twenty-first file was silently missing from the map and from every count.
 * The response's `total` says when even the cap was not enough.
 */
export async function listFiles(sort?: FileListSort): Promise<Paginated<FileSummary>> {
  const { data } = await api.get<Paginated<FileSummary>>('/files', {
    params: { ...(sort ? { sort } : {}), pageSize: MAX_FILE_PAGE_SIZE },
  });
  return data;
}

export async function getFile(id: string): Promise<FileDetail> {
  const { data } = await api.get<FileDetail>(`/files/${id}`);
  return data;
}

/** The file's chain of custody, oldest step first. */
export async function getFileActivity(id: string): Promise<FileActivityEntry[]> {
  const { data } = await api.get<FileActivityEntry[]>(`/files/${id}/activity`);
  return data;
}

export async function getComplaint(id: string): Promise<ComplaintDetail> {
  const { data } = await api.get<ComplaintDetail>(`/complaints/${id}`);
  return data;
}

export async function updateComplaint(
  id: string,
  dto: UpdateComplaintDto,
): Promise<ComplaintDetail> {
  const { data } = await api.patch<ComplaintDetail>(`/complaints/${id}`, dto);
  return data;
}

export async function updateFile(id: string, dto: UpdateFileDto): Promise<FileDetail> {
  const { data } = await api.patch<FileDetail>(`/files/${id}`, dto);
  return data;
}

export async function createFile(dto: CreateFileDto): Promise<FileDetail> {
  const { data } = await api.post<FileDetail>('/files', dto);
  return data;
}

export async function advanceStage(id: string): Promise<FileDetail> {
  const { data } = await api.post<FileDetail>(`/files/${id}/advance-stage`, {});
  return data;
}

export async function saveStage(
  id: string,
  stage: string,
  dto: SaveStageDataDto,
): Promise<FileDetail> {
  const { data } = await api.put<FileDetail>(`/files/${id}/stage/${stage}`, dto);
  return data;
}

/** Stage 1 intake is two writes — a complaint, then the file created from it.
 * Bundled here so the UI submits a single intake form. */
export interface IntakeInput {
  /**
   * An existing complaint to open this file against, instead of creating one.
   *
   * Set when the officer is accepting a report from the public triage queue:
   * that complaint already exists, was written by the person who made the
   * report, and is the evidentiary record of what they said. Creating a second
   * one from the officer's edited version would leave the original orphaned and
   * the report permanently untriaged.
   */
  existingComplaintId?: string;
  source?: CreateComplaintDto['source'];
  intakeMethod?: CreateComplaintDto['intakeMethod'];
  intakeMethodOther?: string;
  referringPerson?: string;
  internalContext?: string;
  anonymous?: boolean;
  complainantName: string;
  complainantPhone?: string;
  complainantAddress?: string;
  complainantCity?: string;
  complainantProvince?: string;
  complainantPostalCode?: string;
  complainantRelationship?: CreateComplaintDto['complainantRelationship'];
  complainantRelationshipOther?: string;
  complainantEmail?: string;
  complainantLocale?: CreateComplaintDto['complainantLocale'];
  description: string;
  address: string;
  bylawCategory?: CreateFileDto['bylawCategory'];
  bylawCategoryOther?: string;
  enforcedLawId?: string;
  priority?: CreateFileDto['priority'];
  priorityReason?: CreateFileDto['priorityReason'];
  priorityReasonOther?: string;
  // Tombstone (Stage 1 spec §3–§6). PAN → many PIDs is one-to-many.
  pan?: string;
  county?: string;
  parcels?: CreateFileDto['parcels'];
  ownerName?: string;
  ownerEmail?: string;
  ownerPhone?: string;
  ownerLocale?: 'en' | 'fr';
  ownerType?: CreateFileDto['ownerType'];
  ownerTypeOther?: string;
  propertyType?: CreateFileDto['propertyType'];
  propertyTypeOther?: string;
  mailingAddress?: string;
  harmonizationStatus?: string;
  propertyDescription?: string;
  taxingAuthority?: string;
  postalCode?: string;
  taxClass?: string;
  neighbourhood?: string;
  ownerUnknown?: boolean;
  // The occupant of the premises, where that is not the owner.
  occupantName?: string;
  occupantPhone?: string;
  occupantAddress?: string;
  occupantCity?: string;
  occupantProvince?: string;
  occupantPostalCode?: string;
  /**
   * The local government whose enforcement this is — the registry anchor the
   * document assembler reads letterhead from. Omitted for a property in a local
   * service district, which has no registry row to point at.
   */
  /**
   * Required: the anchor is the tenant a file belongs to, and an unanchored file
   * would be reachable by nobody but a Munifocus operator — including the
   * officer who opened it. The intake form's submit gate enforces it.
   */
  localGovernmentId: string;
  /**
   * The officer the file is routed to at intake. Only sent alongside an anchor
   * — the API validates the appointment against the registry, and an unanchored
   * file has no registry row to validate against.
   */
  assignedOfficerId?: string;
  /**
   * SNB records the officer extracted from this form before the file existed.
   * The API re-keys their uploads from `staging/` to the new file and attaches
   * them as documents.
   */
  extractionStagingIds?: string[];
  /** Reasons given for the Stage 1 checks those records raised. */
  intakeConfirmations?: CreateFileDto['intakeConfirmations'];
}

export async function intakeFile(input: IntakeInput): Promise<FileDetail> {
  // Accepting a public report: the complaint is already there. The officer may
  // have corrected the address on the form — that belongs to the file, not to
  // what the member of the public said, which stays as submitted.
  if (input.existingComplaintId) {
    return createFile({ ...fileFieldsFrom(input), complaintId: input.existingComplaintId });
  }
  // One request: the API records the complaint and the file together, and
  // removes the complaint again if the file cannot be created. Two calls from
  // here left an orphaned complaint behind every failed file, and the retry
  // recorded the same complaint a second time.
  return createFile({
    ...fileFieldsFrom(input),
    complaint: {
      source: input.source,
      intakeMethod: input.intakeMethod,
      intakeMethodOther: input.intakeMethodOther || undefined,
      referringPerson: input.referringPerson || undefined,
      internalContext: input.internalContext || undefined,
      anonymous: input.anonymous,
      complainantName: input.complainantName,
      complainantPhone: input.complainantPhone || undefined,
      complainantAddress: input.complainantAddress || undefined,
      complainantCity: input.complainantCity || undefined,
      complainantProvince: input.complainantProvince || undefined,
      complainantPostalCode: input.complainantPostalCode || undefined,
      complainantRelationship: input.complainantRelationship,
      complainantRelationshipOther: input.complainantRelationshipOther || undefined,
      complainantEmail: input.complainantEmail || undefined,
      complainantLocale: input.complainantLocale,
      subjectAddress: input.address,
      description: input.description,
    },
  });
}

/**
 * Everything on the intake form that belongs to the FILE rather than to the
 * complaint.
 *
 * Extracted so the two ways a file is opened — a fresh intake, and accepting a
 * report already sitting in the triage queue — build the same payload. They were
 * one object literal inside `intakeFile`; a second copy at the accept path is
 * exactly the kind of duplication that silently stops matching.
 */
function fileFieldsFrom(input: IntakeInput): Omit<CreateFileDto, 'complaintId'> {
  return {
    address: input.address,
    enforcedLawId: input.enforcedLawId,
    bylawCategory: input.bylawCategory,
    bylawCategoryOther: input.bylawCategoryOther || undefined,
    priority: input.priority,
    priorityReason: input.priorityReason,
    priorityReasonOther: input.priorityReasonOther || undefined,
    pan: input.pan || undefined,
    county: input.county || undefined,
    parcels: input.parcels && input.parcels.length > 0 ? input.parcels : undefined,
    intakeConfirmations:
      input.intakeConfirmations && input.intakeConfirmations.length > 0
        ? input.intakeConfirmations
        : undefined,
    ownerName: input.ownerName || undefined,
    ownerEmail: input.ownerEmail || undefined,
    ownerPhone: input.ownerPhone || undefined,
    ownerLocale: input.ownerLocale,
    ownerType: input.ownerType,
    ownerTypeOther: input.ownerTypeOther || undefined,
    propertyType: input.propertyType,
    propertyTypeOther: input.propertyTypeOther || undefined,
    mailingAddress: input.mailingAddress || undefined,
    harmonizationStatus: input.harmonizationStatus || undefined,
    propertyDescription: input.propertyDescription || undefined,
    taxingAuthority: input.taxingAuthority || undefined,
    postalCode: input.postalCode || undefined,
    taxClass: input.taxClass || undefined,
    neighbourhood: input.neighbourhood || undefined,
    ownerUnknown: input.ownerUnknown,
    occupantName: input.occupantName || undefined,
    occupantPhone: input.occupantPhone || undefined,
    occupantAddress: input.occupantAddress || undefined,
    occupantCity: input.occupantCity || undefined,
    occupantProvince: input.occupantProvince || undefined,
    occupantPostalCode: input.occupantPostalCode || undefined,
    // Required: the anchor is the tenant a file belongs to. The submit gate
    // in `useFileForm` guarantees it is set before this runs.
    localGovernmentId: input.localGovernmentId,
    assignedOfficerId: input.localGovernmentId ? input.assignedOfficerId || undefined : undefined,
    extractionStagingIds:
      input.extractionStagingIds && input.extractionStagingIds.length > 0
        ? input.extractionStagingIds
        : undefined,
    // The complaint description seeds the file's work notes so the officer
    // does not start with a blank. They are semantically different — the
    // complaint is the report, the notes are the officer's record — but
    // copying the text here avoids a second "what is this about?" read.
    notes: input.description,
  };
}

// --- React Query hooks -----------------------------------------------------

export const filesKeys = {
  all: ['files'] as const,
  detail: (id: string) => ['files', id] as const,
  activity: (id: string) => ['files', id, 'activity'] as const,
};

/**
 * Files for a list view. Defaults to recency; pass `'priority'` for the Stage 1
 * §F coordinator-queue ordering, which pickers and the map deliberately don't
 * use — they need the file the officer just touched, not the most urgent one.
 */
export function useFiles(sort?: FileListSort) {
  return useQuery({
    queryKey: [...filesKeys.all, sort ?? 'recent'],
    queryFn: () => listFiles(sort),
  });
}

export function useFile(id: string) {
  return useQuery({ queryKey: filesKeys.detail(id), queryFn: () => getFile(id), enabled: !!id });
}

export function useFileActivity(id: string) {
  return useQuery({
    queryKey: filesKeys.activity(id),
    queryFn: () => getFileActivity(id),
    enabled: !!id,
  });
}

export const complaintsKeys = {
  detail: (id: string) => ['complaints', id] as const,
};

export function useComplaint(id: string | null | undefined) {
  return useQuery({
    queryKey: complaintsKeys.detail(id ?? ''),
    queryFn: () => getComplaint(id as string),
    enabled: !!id,
  });
}

/**
 * Correct an intake. Invalidates the file too: the API re-derives `source` from
 * a changed method, and the file screen shows it.
 */
export function useUpdateComplaint(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateComplaintDto) => updateComplaint(id, dto),
    onSuccess: (complaint) => {
      qc.setQueryData(complaintsKeys.detail(id), complaint);
      void qc.invalidateQueries({ queryKey: filesKeys.all });
    },
  });
}

/** Save the file's own fields — everything the complaint does not own. */
export function useUpdateFile(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateFileDto) => updateFile(id, dto),
    onSuccess: (file) => {
      qc.setQueryData(filesKeys.detail(id), file);
      void qc.invalidateQueries({ queryKey: filesKeys.all });
      // Re-assignment moves the lead seat on the file's team.
      void qc.invalidateQueries({ queryKey: adminKeys.fileTeam(id) });
    },
  });
}

export function useIntakeFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: intakeFile,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: filesKeys.all });
      // Opening a file from a public report accepts that report; the queue it
      // came from should stop offering it.
      void qc.invalidateQueries({ queryKey: triageKeys.all });
    },
  });
}

export function useAdvanceStage(id: string) {
  const qc = useQueryClient();
  const toast = useStageToast();
  return useMutation({
    mutationFn: () => advanceStage(id),
    onSuccess: (file) => {
      qc.setQueryData(filesKeys.detail(id), file);
      qc.invalidateQueries({ queryKey: filesKeys.all });
      void qc.invalidateQueries({ queryKey: deadlinesKeys.all });
      toast.saved(file, true);
    },
    onError: (err) => {
      refetchOnConflict(qc, id, err);
      toast.failed(err);
    },
  });
}

export function useSaveStage(id: string, stage: string) {
  const qc = useQueryClient();
  const toast = useStageToast();
  return useMutation({
    // The stage this screen was rendered against travels with the save, so the
    // API can refuse it if the file has moved on in another tab or under a
    // colleague — read from the cached detail, which is what the screen shows.
    mutationFn: (dto: SaveStageDataDto) =>
      saveStage(id, stage, {
        ...dto,
        expectedStage:
          dto.expectedStage ?? qc.getQueryData<FileDetail>(filesKeys.detail(id))?.stage,
      }),
    onSuccess: (file, dto) => {
      qc.setQueryData(filesKeys.detail(id), file);
      qc.invalidateQueries({ queryKey: filesKeys.all });
      // Stage work is where service dates, compliance deadlines and decisions
      // land — each one moves a clock on the deadlines list.
      void qc.invalidateQueries({ queryKey: deadlinesKeys.all });
      // `advance` is what the officer pressed Complete for; a plain save of the
      // stage's own fields is a different, quieter thing.
      toast.saved(file, dto.advance === true);
    },
    onError: (err) => {
      refetchOnConflict(qc, id, err);
      toast.failed(err);
    },
  });
}
